export interface Cluster {
  id: string;
  tpuf_version?: string | null;
  chart_version?: string | null;
  control_plane_version?: string | null;
  possible_operations: PossibleOperations;
  last_reported_replicas?: Record<string, number> | null;
  created_at: Date;
  updated_at: Date;
  operations: Operation[];
  settings_overrides?: Record<string, string> | null;
  settings_overrides_resource_version?: string | null;
  git_details?: GitDetails;
  customer?: string | null;
  customer_id?: string | null;
  /** Lock reason: null = unlocked, "" = locked without reason, "reason" = locked with reason */
  lock_reason?: string | null;
  /** Slack channel ID, or a legacy #name. */
  slack_channel?: string | null;
  /**
   * Cluster nodes grouped by their role, the canonical definition of which is
   * the `MachineRole` enum in `tpuf-ctl-cluster`.
   * Also contains `_metadata` with cluster-level metadata (cloud, region, fips).
   */
  nodes?:
    | (Record<string, PodNodeInfo[]> & { _metadata?: NodesMetadata })
    | null;
  /** When set, this region's billing events roll up to this org. */
  billing_rollup_org_id?: string | null;
  /** When set, overrides the Orb customer billed for the cluster's own events, without an org. */
  billing_cluster_orb_customer_id?: string | null;
  /** User-set tags, merged into `tags` (derived keys win). Edited via the tags PATCH endpoint. */
  custom_tags?: Record<string, string> | null;
  /** Backend-derived tags (kind, customer). These keys can't be overridden. */
  derived_tags?: Record<string, string> | null;
  /**
   * Grouping dimensions keyed by dimension (e.g. `{ cloud: "aws" }`), computed
   * by the control plane. Used to form cohorts in the cluster list.
   */
  tags?: Record<string, string> | null;
  /** Datadog tags identifying this cluster's telemetry, e.g. `{ c: "byoc.acme.prod" }`. */
  datadog_tags?: Record<string, string> | null;
}

// Whether a cluster is customer-owned BYOC, per the backend `kind` tag.
export function isByocCluster(cluster: Cluster): boolean {
  return cluster.tags?.kind === "byoc";
}

export interface PodNodeInfo {
  pod_name: string;
  node_name: string;
  status?: string;
  /**
   * Pod labels/annotations with the turbopuffer prefix, forwarded raw by the
   * agent.
   */
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
}

export const POD_LABELS = {
  queryDeployment: "turbopuffer-query-deployment",
  queryDeploymentTarget: "turbopuffer-query-deployment-target",
  pinningNodeType: "turbopuffer-query-pinning-target-node-type",
  pinningOrgId: "turbopuffer-query-pinning-target-org-id",
} as const;

export const POD_ANNOTATIONS = {
  pinningNamespace: "turbopuffer-query-pinning-target-namespace",
  warmingStatus: "turbopuffer-query-pinning-target-warming-status",
} as const;

/** Name of the TurbopufferQueryDeployment owning this pod, if any. */
export function podDeployment(pod: PodNodeInfo): string | undefined {
  return pod.labels?.[POD_LABELS.queryDeployment];
}

/** Query deployment target (node type) this pod belongs to, if any. The
 * generic target label wins; pinning pods predating the label migration only
 * carry the pinning label. */
export function podTarget(pod: PodNodeInfo): string | undefined {
  return (
    pod.labels?.[POD_LABELS.queryDeploymentTarget] ??
    pod.labels?.[POD_LABELS.pinningNodeType]
  );
}

/** True when the pod belongs to a pinning target (externally scaled). */
export function podPinned(pod: PodNodeInfo): boolean {
  return pod.labels?.[POD_LABELS.pinningNodeType] !== undefined;
}

export type WarmingStatus =
  | "cold"
  | "disk_warming"
  | "memory_warming"
  | "warming"
  | "warm";

/** Cache warming status the placement controller stamps on pinning pods. */
export function podWarmingStatus(pod: PodNodeInfo): WarmingStatus | undefined {
  const status = pod.annotations?.[POD_ANNOTATIONS.warmingStatus];
  return status === "cold" ||
    status === "disk_warming" ||
    status === "memory_warming" ||
    status === "warming" ||
    status === "warm"
    ? status
    : undefined;
}

/** The org/namespace a pinning pod is bound to (stamped by the placement
 * controller), or null when unbound. */
export function podPinnedNamespace(
  pod: PodNodeInfo,
): { orgId: string; namespace: string } | null {
  const orgId = pod.labels?.[POD_LABELS.pinningOrgId];
  const namespace = pod.annotations?.[POD_ANNOTATIONS.pinningNamespace];
  return orgId && namespace ? { orgId, namespace } : null;
}

/** A group of query pods sharing a query deployment target. Pods without
 * target labels (e.g. StatefulSet pods) form a group with no target. */
export interface QueryTargetGroup {
  deployment?: string;
  target?: string;
  pinned: boolean;
  pods: PodNodeInfo[];
  /** Spec replicas from the heartbeat, when known. */
  replicas?: number;
}

export function queryTargetKey({
  pinned,
  deployment,
  target,
}: Pick<QueryTargetGroup, "pinned" | "deployment" | "target">): string {
  return `${pinned}:${deployment ?? ""}:${target ?? ""}`;
}

/** Managed target groups organized under their owning deployment; targetless
 * pods (StatefulSet) form an entry with no deployment. */
export interface QueryDeploymentGroup {
  deployment?: string;
  targets: QueryTargetGroup[];
}

export function groupManagedByDeployment(
  managed: QueryTargetGroup[],
  queryDeployments?: Record<string, QueryDeploymentSummary> | null,
): QueryDeploymentGroup[] {
  const targets = new Map(
    managed.map((group) => [queryTargetKey(group), group]),
  );
  // Spec targets without pods still get a group so an unfulfilled target shows.
  for (const [deployment, summary] of Object.entries(queryDeployments ?? {})) {
    for (const [target, replicas] of Object.entries(summary.targets)) {
      if (replicas === null) continue;
      const key = queryTargetKey({ pinned: false, deployment, target });
      if (replicas === 0 && !targets.has(key)) continue;
      const group = targets.get(key) ?? {
        deployment,
        target,
        pinned: false,
        pods: [],
      };
      targets.set(key, { ...group, replicas });
    }
  }
  const sorted = [...targets.values()].sort(
    (a, b) =>
      (a.deployment ?? "").localeCompare(b.deployment ?? "") ||
      (a.target ?? "").localeCompare(b.target ?? ""),
  );
  const deployments = new Map<string, QueryDeploymentGroup>();
  for (const group of sorted) {
    const key = group.deployment ?? "";
    const deployment = deployments.get(key) ?? {
      deployment: group.deployment,
      targets: [],
    };
    deployment.targets.push(group);
    deployments.set(key, deployment);
  }
  return [...deployments.values()];
}

/**
 * Group query pods by their deployment target, split into operator-managed
 * and pinned (externally scaled) groups.
 */
export function groupQueryPodsByTarget(pods: PodNodeInfo[]): {
  managed: QueryTargetGroup[];
  pinned: QueryTargetGroup[];
} {
  const groups = new Map<string, QueryTargetGroup>();
  for (const pod of pods) {
    const deployment = podDeployment(pod);
    const target = podTarget(pod);
    const pinned = podPinned(pod);
    const key = queryTargetKey({ pinned, deployment, target });
    let group = groups.get(key);
    if (!group) {
      group = { deployment, target, pinned, pods: [] };
      groups.set(key, group);
    }
    group.pods.push(pod);
  }
  const sorted = [...groups.values()].sort(
    (a, b) =>
      (a.target ?? "").localeCompare(b.target ?? "") ||
      (a.deployment ?? "").localeCompare(b.deployment ?? ""),
  );
  const managed = sorted.filter((group) => !group.pinned);
  // Managed pods sort by name so rows don't shuffle between heartbeats.
  for (const group of managed) {
    group.pods.sort((a, b) => a.pod_name.localeCompare(b.pod_name));
  }
  const pinned = sorted.filter((group) => group.pinned);
  // Within a pinned target, order pods by their bound org then namespace so
  // an org's pods sit together; unbound pods go last.
  for (const group of pinned) {
    group.pods.sort((a, b) => {
      const boundA = podPinnedNamespace(a);
      const boundB = podPinnedNamespace(b);
      if (!boundA && !boundB) return a.pod_name.localeCompare(b.pod_name);
      if (!boundA) return 1;
      if (!boundB) return -1;
      return (
        boundA.orgId.localeCompare(boundB.orgId) ||
        boundA.namespace.localeCompare(boundB.namespace) ||
        a.pod_name.localeCompare(b.pod_name)
      );
    });
  }
  return { managed, pinned };
}

export interface NodesMetadata {
  query_deployments?: Record<string, QueryDeploymentSummary>;
}

export interface QueryDeploymentSummary {
  targets: Record<string, number | null>;
}

/** Returns true if every target is operator-managed, as only such targets can
 * support scaling via the control plane. */
export function allTargetsManaged(summary: QueryDeploymentSummary): boolean {
  const replicas = Object.values(summary.targets);
  return replicas.length > 0 && replicas.every((count) => count !== null);
}

export type PossibleOperations = Record<string, ClusterOperation>;

export interface StatusTransition {
  operation_id: string;
  created_at: Date;
  status: OperationStatus;
  extra: any | null;
}

interface OperationLogAttr {
  key: string;
  value: string | number;
}

export interface OperationLog {
  timestamp: Date;
  message: string;
  attrs: OperationLogAttr[];
}

export type OperationStatus =
  | "REQUIRES_APPROVAL"
  | "PENDING"
  | "AWAITING_MAINTENANCE_WINDOW"
  | "RUNNING"
  | "AWAITING_EXTERNAL_EXECUTION"
  | "SUCCESS"
  | "FAILURE"
  | "CANCELLING"
  | "SKIPPED";

export type FleetOperationStatus =
  | "PENDING"
  | "RUNNING"
  | "PAUSED"
  | "RESUMING"
  | "SUCCEEDED"
  | "PARTIALLY_SUCCEEDED"
  | "FAILED"
  | "CANCELED";

export const CONCLUDED_FLEET_STATUSES: FleetOperationStatus[] = [
  "SUCCEEDED",
  "PARTIALLY_SUCCEEDED",
  "FAILED",
  "CANCELED",
];

export interface FleetOperationChild {
  cluster_id: string;
  wave: number;
  status: OperationStatus;
  operation_id: string;
  started_at: Date;
  /** Null until the child settles. */
  ended_at: Date | null;
}

/** A status change on a fleet operation, with who made it and why. */
export interface FleetOperationEvent {
  status: FleetOperationStatus;
  /** A user's email, or "fleet-controller". */
  actor: string;
  reason: string | null;
  created_at: Date;
}

export type FleetSelector = { tag: string } | { ids: string[] };

export interface VersionGate {
  name: string;
  required_min_sha: string;
  target_sha: string;
  scope: "customer" | "cluster";
  description: string;
  tag_url: string;
}

export interface VersionGateDetails extends VersionGate {
  required_min_commit: VersionGateCommit;
  target_commit: VersionGateCommit;
}

export interface VersionGateCommit {
  sha: string;
  message: string;
  date: string | null;
}

export interface VersionGateBlock {
  gate: VersionGate;
  clusters: string[];
}

export interface Fleet {
  name: string;
  selectors: FleetSelector[];
  created_at: Date;
  updated_at: Date;
}

export interface ResolvedFleet {
  waves: { label: string; clusters: string[] }[];
  excluded: { cluster: string; reason: string }[];
}

/** A fleet operation: `operation_name` run across ordered waves of clusters. */
export interface FleetOperation {
  id: string;
  operation_name: string;
  description: string | null;
  arguments: Record<string, unknown>;
  /** Ordered waves of cluster ids. */
  targets: string[][];
  status: FleetOperationStatus;
  created_by: string;
  created_at: Date;
  updated_at: Date;
  slack_thread_ts: string | null;
  /** Null inherits whether this operation kind is gated. */
  gates_enabled: boolean | null;
  /** Null inherits the fleet controller's default bake period. */
  gate_bake_secs: number | null;
  children?: FleetOperationChild[];
  /** Oldest first. Empty for ops that predate the history table. */
  events?: FleetOperationEvent[];
}

export interface ClusterOperation {
  arguments: Record<string, { type: string; required: boolean }>;

  approval_behavior: "auto" | "manual" | "external_execution" | null;
}

export interface Operation {
  id: string;
  cluster_id: string;
  created_at: Date;
  updated_at: Date;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  arguments: any; // Json type in Prisma maps to any in TypeScript
  status_transitions: StatusTransition[];
  operation_logs: OperationLog[];
  /** Set when this operation is a child of a fleet operation. */
  fleet_operation_id?: string | null;
}

export interface GitDetails {
  latest_commit?: GitCommitDetails | null;
  current_commit?: GitCommitDetails | null;
  behind_by: number | null;
}

export interface GitCommitDetails {
  sha: string;
  message: string;
  date: string;
}

/** Newest deployed SaaS SHA plus latest passing master. `on_clusters` lists
 * the clusters already on `commit`. */
export interface LatestDeployed {
  commit: GitCommitDetails | null;
  on_clusters: string[];
  master: GitCommitDetails | null;
}

export interface RegionStats {
  region: string;
  namespace_count: number;
  total_vectors: number;
  total_bytes: number;
  last_active: string | null;
}

export interface SiblingOrg extends OrgSummary {
  customer_id: string | null;
}

export interface Org {
  id: string;
  workos_id: string;
  name: string;
  is_byoc: boolean;
  customer_id: string | null;
  customer_name: string | null;
  customer_slack_channel: string | null;
  purge_at: string | null;
  has_namespaces: boolean;
  region_stats: RegionStats[];
  siblings: SiblingOrg[];
}

/** Represents an org that should be purged, along with details about which region it has data in
    and details about the latest delete-org operation if any. */
export interface OrgToPurge {
  id: string;
  workos_id: string;
  name: string;
  is_byoc: boolean;
  customer_id: string | null;
  customer_name: string | null;
  customer_deactivated_at: string | null;
  purge_at: string;
  purge_regions: OrgPurgeRegion[];
}

/** Details about a region and the latest delete-org operation */
export interface OrgPurgeRegion {
  region: string;
  operation_id: string | null;
  operation_status: string | null;
  operation_created_at: string | null;
}

/** Lightweight org for search results */
export interface OrgSummary {
  id: string;
  workos_id: string;
  name: string;
  is_byoc: boolean;
}

/**
 * Org search result. Includes the linked customer (if any) so results can be
 * grouped under their customer and found by searching the customer's name/id —
 * org names are often just "Prod"/"Dev".
 */
export interface OrgSearchResult extends OrgSummary {
  customer_id: string | null;
  customer_name: string | null;
  /** Null means no heartbeat in the last 24h, not an org holding nothing. */
  stats: OrgStorageTotals | null;
}

/** Storage totals summed across an org's regions. */
export interface OrgStorageTotals {
  namespace_count: number;
  total_vectors: number;
  total_bytes: number;
}

/** Lightweight customer for search results. */
export interface CustomerSearchResult {
  id: string;
  name: string;
}

/** An org attached to a customer, with its aggregate storage stats. */
export interface CustomerOrg extends OrgSummary {
  namespace_count: number;
  total_vectors: number;
  total_bytes: number;
}

/** A customer and all orgs attached to it. */
export interface CustomerDetail {
  id: string;
  name: string;
  workos_org_id: string | null;
  /** Slack channel ID, or a legacy #name. */
  slack_channel: string | null;
  deactivated_at: string | null;
  orgs: CustomerOrg[];
}

/** Full namespace record. */
export interface Namespace {
  id: string;
  org_id: string;
  region: string;
  num_vectors: number;
  billable_bytes: number | null;
  logical_bytes: number | null;
  physical_bytes: number | null;
  created_at: string;
  updated_at: string;
  queried_at: string | null;
  pinned_node_type: string | null;
  pinned_node_type_resolved: string | null;
  pinned_replicas: number | null;
  shards: number;
  sharding_version: number | null;
  stale_at: string | null;
  cmek: number | null;
}

/** Org with usage stats for top orgs display */
export interface OrgWithStats {
  id: string;
  name: string;
  total_bytes: number;
  namespace_count: number;
}

export interface Billing2Migration {
  cluster_id: string;
  cutover_at: string | null;
  rollback_at: string | null;
  billing2_storage_start_at: string | null;
  billing2_storage_start_observed_at: string | null;
  storage_gap_end_at: string | null;
  storage_gap_minted_at: string | null;
}

export interface BillingStorageEvent {
  [key: string]: unknown;
  type: "Storage";
  org_id: string;
  namespace_bytes: number;
  namespace_logical_bytes?: number | null;
  updated_at: string;
  byte_hours?: number | null;
  logical_byte_hours?: number | null;
}

export interface BillingStorageGapEvent {
  gap_start_at: string;
  event: BillingStorageEvent;
}

export interface BillingStorageGap {
  cluster_id: string;
  gap_end_at: string;
  billing2_storage_start_observed_at: string | null;
  events: BillingStorageGapEvent[];
}
